import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, renderList as _renderList, Fragment as _Fragment, vModelSelect as _vModelSelect, withDirectives as _withDirectives, vModelText as _vModelText, withKeys as _withKeys, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode } from "vue"

const _hoisted_1 = { class: "bg-white rounded-lg shadow-md p-4" }
const _hoisted_2 = {
  key: 0,
  class: "flex justify-center items-center py-4"
}
const _hoisted_3 = { key: 1 }
const _hoisted_4 = { class: "mb-8" }
const _hoisted_5 = { class: "bg-gray-50 p-4 rounded-lg border border-gray-200 mb-4" }
const _hoisted_6 = {
  key: 0,
  class: "flex items-center py-2"
}
const _hoisted_7 = {
  key: 1,
  class: "flex items-center justify-between bg-white p-3 rounded border border-gray-200 mb-3"
}
const _hoisted_8 = { class: "flex items-center" }
const _hoisted_9 = { class: "text-gray-800" }
const _hoisted_10 = ["disabled"]
const _hoisted_11 = {
  key: 2,
  class: "text-gray-500 italic mb-3"
}
const _hoisted_12 = { class: "relative" }
const _hoisted_13 = {
  key: 0,
  class: "flex items-center py-2"
}
const _hoisted_14 = {
  key: 1,
  class: "text-gray-500 italic"
}
const _hoisted_15 = {
  key: 2,
  class: "flex flex-wrap gap-2 items-center"
}
const _hoisted_16 = { class: "flex-1 min-w-[200px]" }
const _hoisted_17 = ["disabled"]
const _hoisted_18 = ["value"]
const _hoisted_19 = ["disabled"]
const _hoisted_20 = { key: 0 }
const _hoisted_21 = { key: 1 }
const _hoisted_22 = { class: "mb-4" }
const _hoisted_23 = {
  key: 0,
  class: "text-gray-500 italic"
}
const _hoisted_24 = {
  key: 1,
  class: "flex flex-wrap gap-2"
}
const _hoisted_25 = { class: "text-gray-800 mr-2" }
const _hoisted_26 = ["onClick", "disabled"]
const _hoisted_27 = { class: "mb-2" }
const _hoisted_28 = {
  key: 0,
  class: "bg-red-50 border-l-4 border-red-500 p-3 mb-3 text-sm"
}
const _hoisted_29 = { class: "text-red-700" }
const _hoisted_30 = { class: "flex flex-wrap gap-2 items-center" }
const _hoisted_31 = { class: "flex-1 min-w-[200px] relative" }
const _hoisted_32 = ["disabled"]
const _hoisted_33 = {
  key: 0,
  class: "absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto"
}
const _hoisted_34 = {
  key: 0,
  class: "p-2 text-center text-gray-500"
}
const _hoisted_35 = {
  key: 1,
  class: "p-4 text-center text-gray-500"
}
const _hoisted_36 = { key: 2 }
const _hoisted_37 = ["onClick"]
const _hoisted_38 = ["disabled"]
const _hoisted_39 = { key: 0 }
const _hoisted_40 = { key: 1 }

import { ref, computed, onMounted } from 'vue';
import { systemConfigService } from '@/features/system-config';
import { supabase } from '@/services/supabase';
import { logger, LogCategory } from '@/services/logger';

// Define a simple interface for the file objects we need
interface FileTemplate {
  id: string;
  name: string;
  type: string;
  url: string;
}

// State

export default /*@__PURE__*/_defineComponent({
  __name: 'SystemConfiguration',
  setup(__props) {

const loading = ref(true);
const error = ref(false);
const errorMessage = ref('');
const configData = ref<any[]>([]);
const newAttendeeEmail = ref('');
const addingAttendee = ref(false);
const profileEmails = ref<string[]>([]);
const filteredEmails = ref<string[]>([]);
const showEmailDropdown = ref(false);
const isLoadingEmails = ref(false);

// Template reference state
const xlsxTemplates = ref<FileTemplate[]>([]);
const loadingTemplates = ref(false);
const selectedTemplateId = ref('');
const updatingTemplate = ref(false);

// Computed properties
const boardMeetingAttendees = computed(() => {
  const config = configData.value.find(item => item.property === 'board_meeting_attendees');
  return config ? config.value : [];
});

// Reference template computed properties
const referenceTemplateId = computed(() => {
  const config = configData.value.find(item => item.property === 'reference_empty_template');
  return config ? config.value : null;
});

const referenceTemplateName = computed(() => {
  if (!referenceTemplateId.value) return '';
  
  const template = xlsxTemplates.value.find(t => t.id === referenceTemplateId.value);
  return template ? template.name : 'Unknown template';
});

// Computed property for display emails
const displayEmails = computed(() => {
  if (!newAttendeeEmail.value.trim()) {
    // Show all emails when input is empty
    return profileEmails.value;
  }
  
  // Show filtered emails when user is typing
  return filteredEmails.value;
});

// Methods
const fetchConfig = async () => {
  loading.value = true;
  error.value = false;
  errorMessage.value = '';
  
  try {
    // Make a direct fetch to see the raw response
    const rawResponse = await fetch((process.env.VUE_APP_FISSION_URL || 'https://ai-core.dev.azcld.advisori.de/fission/fraport/api/v1/') + 'system-config');
    const rawData = await rawResponse.json();
    console.log('Raw API response:', rawData);
    
    const response = await systemConfigService.getAllConfig();
    console.log('System config service response:', response);
    
    // Also try to fetch directly from Supabase
    const { data: supabaseData, error: supabaseError } = await supabase
      .from('system_configuration')
      .select('*');
    
    if (supabaseError) {
      console.error('Error fetching from Supabase:', supabaseError);
    } else {
      console.log('Direct Supabase fetch result:', supabaseData);
      
      // Check if reference_empty_template exists in Supabase data
      const referenceTemplateInSupabase = supabaseData?.some(item =>
        item.property === 'reference_empty_template' && item.value !== null
      );
      
      console.log('Reference template in Supabase:', referenceTemplateInSupabase);
      
      // If it exists in Supabase but not in API response, add it to configData
      if (referenceTemplateInSupabase) {
        const templateConfig = supabaseData.find(item => item.property === 'reference_empty_template');
        if (templateConfig && templateConfig.value) {
          console.log('Adding reference template from Supabase:', templateConfig);
          
          // Add to configData if not already there
          if (!response.some(item => item.property === 'reference_empty_template')) {
            response.push(templateConfig);
          }
        }
      }
    }
    
    configData.value = response;
    
    // Log the configuration data for debugging
    logger.info('Fetched configuration data', LogCategory.GENERAL, {
      configCount: configData.value.length,
      configData: configData.value
    });
    
    // Check if reference_empty_template exists
    const hasReferenceTemplate = configData.value.some(item => item.property === 'reference_empty_template');
    console.log('Has reference template property:', hasReferenceTemplate);
  } catch (err: any) {
    error.value = true;
    errorMessage.value = err.message || 'Failed to load configuration';
    console.error('Error fetching configuration:', err);
  } finally {
    loading.value = false;
  }
};

// Validate email format
const isValidEmail = (email: string): boolean => {
  // Basic validation to check if email contains @ symbol
  if (!email.includes('@')) {
    return false;
  }
  
  // More comprehensive validation using regex
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

const addAttendee = async () => {
  if (!newAttendeeEmail.value || addingAttendee.value) return;
  
  // Validate email format
  if (!isValidEmail(newAttendeeEmail.value)) {
    error.value = true;
    errorMessage.value = 'Invalid email format. Please enter a valid email address.';
    return;
  }
  
  addingAttendee.value = true;
  error.value = false;
  errorMessage.value = '';
  
  try {
    // Save the current configuration data
    const currentConfig = [...configData.value];
    
    // Create a new array with the added email
    const updatedAttendees = [...boardMeetingAttendees.value, newAttendeeEmail.value];
    
    // Prepare the configurations to update
    const configs = [
      { property: 'board_meeting_attendees', value: updatedAttendees }
    ];
    
    // If reference_empty_template exists, include it in the update
    const referenceTemplateConfig = currentConfig.find(item => item.property === 'reference_empty_template');
    if (referenceTemplateConfig) {
      configs.push({ property: 'reference_empty_template', value: referenceTemplateConfig.value });
    }
    
    // Update both configurations at once
    await systemConfigService.updateMultipleConfigs(configs);
    
    // Refresh the configuration data
    await fetchConfig();
    
    // Clear the input
    newAttendeeEmail.value = '';
  } catch (err: any) {
    error.value = true;
    errorMessage.value = err.message || 'Failed to add attendee';
    console.error('Error adding attendee:', err);
  } finally {
    addingAttendee.value = false;
  }
};

const removeAttendee = async (index: number) => {
  try {
    // Save the current configuration data
    const currentConfig = [...configData.value];
    
    // Create a new array without the removed email
    const updatedAttendees = boardMeetingAttendees.value.filter((_, i) => i !== index);
    
    // Prepare the configurations to update
    const configs = [
      { property: 'board_meeting_attendees', value: updatedAttendees }
    ];
    
    // If reference_empty_template exists, include it in the update
    const referenceTemplateConfig = currentConfig.find(item => item.property === 'reference_empty_template');
    if (referenceTemplateConfig) {
      configs.push({ property: 'reference_empty_template', value: referenceTemplateConfig.value });
    }
    
    // Update both configurations at once
    await systemConfigService.updateMultipleConfigs(configs);
    
    // Refresh the configuration data
    await fetchConfig();
  } catch (err: any) {
    error.value = true;
    errorMessage.value = err.message || 'Failed to remove attendee';
    console.error('Error removing attendee:', err);
  }
};

// Fetch emails from profiles table
const fetchProfileEmails = async () => {
  isLoadingEmails.value = true;
  try {
    const { data, error } = await supabase
      .from('profiles')
      .select('email')
      .not('email', 'is', null);
    
    if (error) {
      throw error;
    }
    
    // Extract emails and filter out nulls
    profileEmails.value = data
      .map(profile => profile.email)
      .filter(email => email) as string[];
    
    logger.info('Fetched profile emails for autocomplete', LogCategory.GENERAL, {
      count: profileEmails.value.length
    });
  } catch (err: any) {
    logger.error('Error fetching profile emails', LogCategory.GENERAL, { error: err });
    console.error('Error fetching profile emails:', err);
  } finally {
    isLoadingEmails.value = false;
  }
};

// Handle focus on the email field
const handleFocus = async () => {
  showEmailDropdown.value = true;
  
  // If we haven't loaded emails yet, load them now
  if (profileEmails.value.length === 0) {
    await fetchProfileEmails();
  }
};

// Handle blur on the email field
const handleBlur = () => {
  // Use window.setTimeout instead of setTimeout directly
  window.setTimeout(() => {
    showEmailDropdown.value = false;
  }, 200);
};

// Handle input in the email field
const handleEmailInput = () => {
  // Filter emails based on input
  const input = newAttendeeEmail.value.toLowerCase().trim();
  
  if (input === '') {
    // When input is empty, we'll show all emails (handled by displayEmails computed property)
    filteredEmails.value = [];
    return;
  }
  
  // Filter emails that START with the input text
  filteredEmails.value = profileEmails.value
    .filter(email => email.toLowerCase().startsWith(input));
};

// Select an email from the dropdown
const selectEmail = (email: string) => {
  newAttendeeEmail.value = email;
  showEmailDropdown.value = false;
};

// Template methods
const fetchTemplates = async () => {
  loadingTemplates.value = true;
  try {
    const { data, error } = await supabase
      .from('files')
      .select('id, name, type, url')
      .eq('type', 'xlsx');
    
    if (error) {
      throw error;
    }
    
    xlsxTemplates.value = data as FileTemplate[] || [];
    logger.info('Fetched XLSX templates', LogCategory.GENERAL, {
      count: xlsxTemplates.value.length
    });
  } catch (err: any) {
    logger.error('Error fetching XLSX templates', LogCategory.GENERAL, { error: err });
    console.error('Error fetching XLSX templates:', err);
  } finally {
    loadingTemplates.value = false;
  }
};

const setReferenceTemplate = async () => {
  if (!selectedTemplateId.value || updatingTemplate.value) return;
  
  updatingTemplate.value = true;
  error.value = false;
  errorMessage.value = '';
  
  try {
    // We don't need to save the current configuration data anymore
    
    console.log('Attempting to save reference_empty_template directly to Supabase');
    
    // Try to directly insert into the system_configuration table
    const { data: existingConfig, error: fetchError } = await supabase
      .from('system_configuration')
      .select('*')
      .eq('property', 'reference_empty_template')
      .single();
    
    let result;
    
    if (fetchError && fetchError.code !== 'PGRST116') {
      console.error('Error checking for existing config:', fetchError);
      throw fetchError;
    }
    
    if (existingConfig) {
      // Update existing record
      console.log('Updating existing record:', existingConfig);
      const { data, error } = await supabase
        .from('system_configuration')
        .update({ value: selectedTemplateId.value })
        .eq('property', 'reference_empty_template')
        .select();
      
      if (error) {
        console.error('Error updating config:', error);
        throw error;
      }
      
      result = data;
    } else {
      // Insert new record
      console.log('Inserting new record for reference_empty_template');
      const { data, error } = await supabase
        .from('system_configuration')
        .insert({
          property: 'reference_empty_template',
          value: selectedTemplateId.value
        })
        .select();
      
      if (error) {
        console.error('Error inserting config:', error);
        throw error;
      }
      
      result = data;
    }
    
    console.log('Direct Supabase operation result:', result);
    
    // Also try the API method as a backup
    try {
      const updateResult = await systemConfigService.updateConfig('reference_empty_template', selectedTemplateId.value);
      console.log('API update result:', updateResult);
    } catch (apiError) {
      console.error('API update failed, but direct DB update succeeded:', apiError);
    }
    
    // Refresh the configuration data
    await fetchConfig();
    
    // Clear the selection
    selectedTemplateId.value = '';
    
    logger.info('Reference template set successfully', LogCategory.GENERAL, {
      templateId: selectedTemplateId.value
    });
  } catch (err: any) {
    error.value = true;
    errorMessage.value = err.message || 'Failed to set reference template';
    console.error('Error setting reference template:', err);
  } finally {
    updatingTemplate.value = false;
  }
};

const removeReferenceTemplate = async () => {
  updatingTemplate.value = true;
  error.value = false;
  errorMessage.value = '';
  
  try {
    // We don't need to save the current configuration data anymore
    
    console.log('Attempting to remove reference_empty_template directly from Supabase');
    
    // Try to directly update the system_configuration table
    const { data, error } = await supabase
      .from('system_configuration')
      .update({ value: null })
      .eq('property', 'reference_empty_template')
      .select();
    
    if (error) {
      console.error('Error removing config:', error);
      throw error;
    }
    
    console.log('Direct Supabase operation result:', data);
    
    // Also try the API method as a backup
    try {
      const updateResult = await systemConfigService.updateConfig('reference_empty_template', null);
      console.log('API update result:', updateResult);
    } catch (apiError) {
      console.error('API update failed, but direct DB update succeeded:', apiError);
    }
    
    // Refresh the configuration data
    await fetchConfig();
    
    logger.info('Reference template removed successfully', LogCategory.GENERAL);
  } catch (err: any) {
    error.value = true;
    errorMessage.value = err.message || 'Failed to remove reference template';
    console.error('Error removing reference template:', err);
  } finally {
    updatingTemplate.value = false;
  }
};

// Fetch configuration data when component is mounted
onMounted(() => {
  fetchConfig();
  fetchProfileEmails();
  fetchTemplates();
});

return (_ctx: any,_cache: any) => {
  const _component_router_link = _resolveComponent("router-link")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    (loading.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_2, _cache[4] || (_cache[4] = [
          _createElementVNode("div", { class: "animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600" }, null, -1),
          _createElementVNode("span", { class: "ml-2 text-gray-700 text-sm" }, "Loading...", -1)
        ])))
      : (_openBlock(), _createElementBlock("div", _hoisted_3, [
          _createElementVNode("div", _hoisted_4, [
            _cache[11] || (_cache[11] = _createElementVNode("h4", { class: "text-lg font-bold mb-3 text-gray-900" }, "Reference Template File", -1)),
            _createElementVNode("div", _hoisted_5, [
              _cache[10] || (_cache[10] = _createElementVNode("p", { class: "text-sm text-gray-600 mb-2" }, " Select an XLSX file to use as the reference \"empty template\" for template generation. ", -1)),
              (loading.value)
                ? (_openBlock(), _createElementBlock("div", _hoisted_6, _cache[5] || (_cache[5] = [
                    _createElementVNode("div", { class: "animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2" }, null, -1),
                    _createElementVNode("span", { class: "text-gray-700 text-sm" }, "Loading template information...", -1)
                  ])))
                : (referenceTemplateId.value)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_7, [
                      _createElementVNode("div", _hoisted_8, [
                        _cache[6] || (_cache[6] = _createElementVNode("svg", {
                          class: "h-5 w-5 text-green-500 mr-2",
                          fill: "none",
                          viewBox: "0 0 24 24",
                          stroke: "currentColor"
                        }, [
                          _createElementVNode("path", {
                            "stroke-linecap": "round",
                            "stroke-linejoin": "round",
                            "stroke-width": "2",
                            d: "M5 13l4 4L19 7"
                          })
                        ], -1)),
                        _createElementVNode("span", _hoisted_9, _toDisplayString(referenceTemplateName.value), 1)
                      ]),
                      _createElementVNode("button", {
                        onClick: removeReferenceTemplate,
                        class: "text-red-500 hover:text-red-700 focus:outline-none",
                        title: "Remove reference template",
                        disabled: loading.value || updatingTemplate.value
                      }, _cache[7] || (_cache[7] = [
                        _createElementVNode("svg", {
                          class: "h-5 w-5",
                          fill: "none",
                          viewBox: "0 0 24 24",
                          stroke: "currentColor"
                        }, [
                          _createElementVNode("path", {
                            "stroke-linecap": "round",
                            "stroke-linejoin": "round",
                            "stroke-width": "2",
                            d: "M6 18L18 6M6 6l12 12"
                          })
                        ], -1)
                      ]), 8, _hoisted_10)
                    ]))
                  : (_openBlock(), _createElementBlock("div", _hoisted_11, " No reference template selected ")),
              _createElementVNode("div", _hoisted_12, [
                (loadingTemplates.value)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_13, _cache[8] || (_cache[8] = [
                      _createElementVNode("div", { class: "animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2" }, null, -1),
                      _createElementVNode("span", { class: "text-gray-700 text-sm" }, "Loading available templates...", -1)
                    ])))
                  : (xlsxTemplates.value.length === 0)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_14, " No XLSX templates available "))
                    : (_openBlock(), _createElementBlock("div", _hoisted_15, [
                        _createElementVNode("div", _hoisted_16, [
                          _withDirectives(_createElementVNode("select", {
                            "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((selectedTemplateId).value = $event)),
                            class: "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500",
                            disabled: loading.value || updatingTemplate.value
                          }, [
                            _cache[9] || (_cache[9] = _createElementVNode("option", {
                              value: "",
                              disabled: ""
                            }, "Select a template", -1)),
                            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(xlsxTemplates.value, (template) => {
                              return (_openBlock(), _createElementBlock("option", {
                                key: template.id,
                                value: template.id
                              }, _toDisplayString(template.name), 9, _hoisted_18))
                            }), 128))
                          ], 8, _hoisted_17), [
                            [_vModelSelect, selectedTemplateId.value]
                          ])
                        ]),
                        _createElementVNode("button", {
                          onClick: setReferenceTemplate,
                          disabled: !selectedTemplateId.value || loading.value || updatingTemplate.value,
                          class: "px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                        }, [
                          (updatingTemplate.value)
                            ? (_openBlock(), _createElementBlock("span", _hoisted_20, "Setting..."))
                            : (_openBlock(), _createElementBlock("span", _hoisted_21, "Set as Reference"))
                        ], 8, _hoisted_19)
                      ]))
              ])
            ])
          ]),
          _createElementVNode("div", null, [
            _cache[15] || (_cache[15] = _createElementVNode("h4", { class: "text-lg font-bold mb-3 text-gray-900" }, "Board Meeting Attendees", -1)),
            _createElementVNode("div", _hoisted_22, [
              (boardMeetingAttendees.value.length === 0)
                ? (_openBlock(), _createElementBlock("div", _hoisted_23, " No attendees configured "))
                : (_openBlock(), _createElementBlock("ul", _hoisted_24, [
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(boardMeetingAttendees.value, (email, index) => {
                      return (_openBlock(), _createElementBlock("li", {
                        key: index,
                        class: "flex items-center bg-gray-50 py-2 px-3 rounded border border-gray-200"
                      }, [
                        _createElementVNode("span", _hoisted_25, _toDisplayString(email), 1),
                        _createElementVNode("button", {
                          onClick: ($event: any) => (removeAttendee(index)),
                          class: "text-red-500 hover:text-red-700 focus:outline-none",
                          title: "Remove attendee",
                          disabled: loading.value
                        }, _cache[12] || (_cache[12] = [
                          _createElementVNode("svg", {
                            class: "h-5 w-5",
                            fill: "none",
                            viewBox: "0 0 24 24",
                            stroke: "currentColor"
                          }, [
                            _createElementVNode("path", {
                              "stroke-linecap": "round",
                              "stroke-linejoin": "round",
                              "stroke-width": "2",
                              d: "M6 18L18 6M6 6l12 12"
                            })
                          ], -1)
                        ]), 8, _hoisted_26)
                      ]))
                    }), 128))
                  ]))
            ]),
            _createElementVNode("div", _hoisted_27, [
              (error.value)
                ? (_openBlock(), _createElementBlock("div", _hoisted_28, [
                    _createElementVNode("p", _hoisted_29, _toDisplayString(errorMessage.value), 1)
                  ]))
                : _createCommentVNode("", true),
              _createElementVNode("div", _hoisted_30, [
                _createElementVNode("div", _hoisted_31, [
                  _withDirectives(_createElementVNode("input", {
                    "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((newAttendeeEmail).value = $event)),
                    type: "email",
                    placeholder: "Enter email address",
                    class: "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500",
                    disabled: loading.value || addingAttendee.value,
                    onKeyup: _cache[2] || (_cache[2] = _withKeys(($event: any) => (newAttendeeEmail.value && addAttendee()), ["enter"])),
                    onInput: _cache[3] || (_cache[3] = ($event: any) => {handleEmailInput(); error.value = false; errorMessage.value = '';}),
                    onFocus: handleFocus,
                    onBlur: handleBlur
                  }, null, 40, _hoisted_32), [
                    [_vModelText, newAttendeeEmail.value]
                  ]),
                  (showEmailDropdown.value)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_33, [
                        (isLoadingEmails.value)
                          ? (_openBlock(), _createElementBlock("div", _hoisted_34, _cache[13] || (_cache[13] = [
                              _createElementVNode("div", { class: "animate-spin inline-block rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2" }, null, -1),
                              _createTextVNode(" Loading... ")
                            ])))
                          : (displayEmails.value.length === 0)
                            ? (_openBlock(), _createElementBlock("div", _hoisted_35, " No matching emails found "))
                            : (_openBlock(), _createElementBlock("ul", _hoisted_36, [
                                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(displayEmails.value, (email, index) => {
                                  return (_openBlock(), _createElementBlock("li", {
                                    key: index,
                                    onClick: ($event: any) => (selectEmail(email)),
                                    class: "px-4 py-2 hover:bg-gray-100 cursor-pointer text-gray-700"
                                  }, _toDisplayString(email), 9, _hoisted_37))
                                }), 128))
                              ]))
                      ]))
                    : _createCommentVNode("", true)
                ]),
                _createElementVNode("button", {
                  onClick: addAttendee,
                  disabled: !newAttendeeEmail.value || loading.value || addingAttendee.value,
                  class: "px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                }, [
                  (addingAttendee.value)
                    ? (_openBlock(), _createElementBlock("span", _hoisted_39, "Adding..."))
                    : (_openBlock(), _createElementBlock("span", _hoisted_40, "Add"))
                ], 8, _hoisted_38),
                _createVNode(_component_router_link, {
                  to: "/administration",
                  class: "px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                }, {
                  default: _withCtx(() => _cache[14] || (_cache[14] = [
                    _createTextVNode(" Return to Administration ")
                  ])),
                  _: 1,
                  __: [14]
                })
              ])
            ])
          ])
        ]))
  ]))
}
}

})